/**
 * @typedef {object} PositionInfo
 * @property {number} Row
 * @property {number} Column
 * @property {number} Rows
 * @property {number} Columns
 */

/**
 * @typedef {object} CellLayoutConfig
 * @property {string} Name
 * @property {PositionInfo} PositionInfo
 */

/**
 * @typedef {object} ControlCfg
 * @property {string} ColHint
 * @property {string} ColTitle
 * @property {string} Default
 * @property {string} DefaultValue
 * @property {string} IsReadOnly
 * @property {string} IsRequired
 * @property {'1' | '0'} IsShow
 * @property {string} OnwerUserFormID
 * @property {string} OnwerUserFormOnlyKey
 * @property {string} OnwerUserFormType
 * @property {string} RegexContent
 * @property {string} RegexErrorMsg
 */

/**
 * @typedef {object} ColInfo
 * @property {string} ColAlign
 * @property {string} ColControlName
 * @property {string} ColControlTypeID
 * @property {string} ColFormat
 * @property {string} ColHint
 * @property {string} ColName
 * @property {string} ColTitle
 * @property {string} ColWidth
 * @property {ControlCfg} ControlCfg
 * @property {string} DefaultValue
 * @property {string} IsAllowSearch
 * @property {string} IsCanSort
 * @property {string} IsRequired
 * @property {string} IsShowHtml
 * @property {string} IsShowInEdit
 * @property {string} IsShowInGrid
 * @property {string} RegexContent
 * @property {string} RegexErrorMsg
 * @property {string} RegexID
 * @property {string} SortID
 * @property {string} UserFormColID
 */
import './index.less';
import { cloneDeep, curry } from 'lodash';
import { Grid, Cell } from './components/grid/index';
import { positionComparator, sameColumn } from './utils';
import CellContent from './components/cell-content';
import EditLayout from './components/edit-layout';
import { position } from '../Grid/directives';

export default {
    name: 'Grid',
    props: {
        layoutConfig: {
            type: Object
        },
        colInfoList: {
            type: Array,
            default: () => []
        },
        isCustomLayoutMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            handledConfig: null
        };
    },
    computed: {
        showControlConfig() {
            return (this.handledConfig?.Children ?? [])
                .filter(
                    item =>
                        item?.ColInfo &&
                        item.ColInfo?.ControlCfg?.IsShow !== '0'
                )
                .sort(positionComparator);
        },
        innerConfig() {
            return this.showControlConfig.concat(
                (this.handledConfig?.Children ?? [])
                    .filter(
                        item =>
                            item?.ColInfo &&
                            item?.ColInfo?.ControlCfg?.IsShow === '0'
                    )
                    .sort(positionComparator)
            );
        }
    },
    watch: {
        showControlConfig: {
            handler() {
                for (
                    let i = 0, length = this.showControlConfig.length;
                    i < length;
                    i++
                ) {
                    /** @type {CellLayoutConfig[]} */
                    const prevControlList = this.showControlConfig.slice(0, i);
                    const item = this.showControlConfig[i];
                    if (prevControlList.length) {
                        /** @type {CellLayoutConfig} */
                        item.PositionInfo.Row = Math.max(
                            ...prevControlList
                                .filter(itm => sameColumn(item, itm))
                                .map(
                                    item =>
                                        item.PositionInfo.Row +
                                        item.PositionInfo.Rows
                                ),
                            1
                        );
                    } else if (i === 0) {
                        // 首行隐藏
                        item.PositionInfo.Row = 1;
                    }
                }
            },
            immediate: true
        },
        colInfoList: {
            handler() {
                this.init();
            }
        }
    },
    created() {
        this.init();
    },
    methods: {
        init() {
            let layoutConfig = cloneDeep(this.layoutConfig);
            layoutConfig.Children.forEach(child => {
                child.ColInfo = this.colInfoList?.find(
                    colInfo => colInfo.ColName === child.Name
                );
            });

            const notExistColNameList = new Map();

            const matchColName = curry(
                /**
                 * @param {string} colName
                 * @param {ColInfo} colInfo
                 * @returns {boolean}
                 */
                (colName, layoutConfig) => layoutConfig.Name === colName
            );

            this.colInfoList.forEach(colInfo => {
                if (
                    !(layoutConfig.Children ?? []).find(
                        matchColName(colInfo.ColName)
                    )
                ) {
                    notExistColNameList.set(colInfo.ColName, colInfo);
                }
            });

            const getMaxEndRow = layoutConfig => {
                let max = 1;
                let list = [layoutConfig];
                for (let i = 0; i < list.length; i++) {
                    const item = list[i];
                    max = Math.max(
                        item.PositionInfo.Row + item.PositionInfo.Rows,
                        max
                    );
                    if (Array.isArray(item.Children)) {
                        list = list.concat(item.Children);
                    }
                }

                return max;
            };

            notExistColNameList.forEach((value, key) => {
                layoutConfig.Children.push({
                    Name: key,
                    ColInfo: value,
                    PositionInfo: {
                        Column: 1,
                        Row: getMaxEndRow(layoutConfig),
                        Columns: 24,
                        Rows: 1
                    }
                });
            });

            layoutConfig.Children = layoutConfig.Children.filter(
                child => child.ColInfo
            );

            this.handledConfig = layoutConfig;
        },
        /**
         * @param {CellLayoutConfig & { ColInfo: ColInfo }} layoutConfig
         */
        RenderCell(layoutConfig) {
            return (
                <Cell
                    style={{ display: layoutConfig?.ColInfo?.ControlCfg?.IsShow !== '0' ? "block" : "none" }}
                    data-col-name={layoutConfig.ColInfo.ColName}
                    key={layoutConfig.ColInfo.ColName}
                    layoutConfig={layoutConfig}
                >
                    {this.renderCellContent(layoutConfig)}
                </Cell>
            );
        },
        /**
         * @param {CellLayoutConfig & { ColInfo: ColInfo }} layoutConfig
         */
        renderCellContent(layoutConfig) {
            return (
                <CellContent
                    layoutConfig={layoutConfig}
                    scopedSlots={this.$scopedSlots}
                />
            );
        }
    },
    render(h) {
        const children = this.innerConfig.map(item =>
            this.RenderCell(item)
        );
        
        return (
            <div class="form-layout">
                <Grid>{children}</Grid>
                {this.isCustomLayoutMode ? (
                    <EditLayout layoutConfig={this.layoutConfig.Children} />
                ) : null}
            </div>
        );
    }
};
